import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Droplets, Cookie, Sun, Moon, TrendingUp, Info, Printer, FileText } from "lucide-react";
import { Reveal, Label } from "./motion";
import { LOGO_LIGHT } from "./Logo";

const STEPS = [
  { icon: Sun, title: "Start low & slow", body: "Begin at the gentle end of the range once daily and watch how your poodle responds over a week." },
  { icon: TrendingUp, title: "Adjust gradually", body: "Increase slowly toward comfort as needed — small poodles need far less than standards." },
  { icon: Moon, title: "Build a routine", body: "Consistency helps most. Many owners pair a morning drop with an evening chew." },
];

const KG = 2.2046;
const fmtN = (n) => (n < 1 ? n.toFixed(2) : n.toFixed(1));

const BANDS = [
  { label: "Toy", low: 3, high: 10, doodles: "Toy Poodle · Maltipoo · Cavapoo" },
  { label: "Miniature", low: 10, high: 20, doodles: "Mini Poodle · Cockapoo · Poochon" },
  { label: "Small / Medium", low: 20, high: 35, doodles: "Mini Goldendoodle · Cavoodle" },
  { label: "Standard", low: 35, high: 55, doodles: "Standard Poodle · Aussiedoodle" },
  { label: "Large Doodle", low: 55, high: 90, doodles: "Goldendoodle · Bernedoodle · Sheepadoodle" },
];

const buildRows = () =>
  BANDS.map((b) => {
    const mgLow = (b.low / KG) * 0.1;
    const mgHigh = (b.high / KG) * 0.5;
    const chewsLow = Math.max(1, Math.round(mgLow / 2.5));
    const chewsHigh = Math.max(chewsLow, Math.round(mgHigh / 2.5));
    return {
      ...b,
      cbd: `${fmtN(mgLow)}–${fmtN(mgHigh)} mg`,
      chews: chewsLow === chewsHigh ? `${chewsLow} / day` : `${chewsLow}–${chewsHigh} / day`,
    };
  });

const CHART_ROWS = buildRows();

const printChart = () => {
  const w = window.open("", "_blank", "width=840,height=1080");
  if (!w) return;
  const rowsHtml = CHART_ROWS.map(
    (r) => `<tr>
      <td><strong>${r.label}</strong><div class="sub">${r.low}–${r.high} lbs · ${(r.low / KG).toFixed(1)}–${(r.high / KG).toFixed(1)} kg</div></td>
      <td class="doodles">${r.doodles}</td>
      <td class="mid">${r.cbd}</td>
      <td class="mid">${r.chews}</td>
    </tr>`
  ).join("");
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>CBDoodle Dosing Chart</title>
  <style>
    @page { margin: 18mm; }
    * { box-sizing: border-box; }
    body { font-family: Georgia, 'Times New Roman', serif; color: #2C2B29; margin: 0; padding: 32px; background: #FAF7F2; }
    .head { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #C58F3B; padding-bottom: 18px; }
    .head img { width: 84px; height: 84px; object-fit: contain; }
    .head h1 { font-size: 26px; margin: 0; }
    .head p { margin: 4px 0 0; font-family: Arial, sans-serif; font-size: 12px; letter-spacing: .12em; text-transform: uppercase; color: #4A6B5D; }
    .intro { font-family: Arial, sans-serif; font-size: 13px; line-height: 1.6; color: #5C5850; margin: 18px 0 20px; }
    table { width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; }
    th { background: #4A6B5D; color: #FAF7F2; text-align: left; font-size: 11px; letter-spacing: .1em; text-transform: uppercase; padding: 10px 12px; }
    td { border-bottom: 1px solid #E6E0D4; padding: 12px; font-size: 13px; vertical-align: top; }
    td.mid { font-weight: bold; color: #2D4338; }
    td .sub { font-size: 11px; color: #8A8478; font-weight: normal; margin-top: 3px; }
    td.doodles { color: #5C5850; font-size: 12px; }
    tr:nth-child(even) td { background: #FFFDF9; }
    .note { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.6; color: #7a5c22; background: #FDF6E7; border: 1px solid #C58F3B; border-radius: 8px; padding: 12px 14px; margin-top: 20px; }
    .foot { font-family: Arial, sans-serif; font-size: 10px; color: #8A8478; margin-top: 18px; text-align: center; }
    .vet { font-family: Arial, sans-serif; font-size: 12px; margin-top: 22px; display: flex; gap: 40px; }
    .vet div { flex: 1; border-top: 1px solid #2C2B29; padding-top: 6px; color: #5C5850; }
  </style></head><body>
    <div class="head">
      <img src="${LOGO_LIGHT}" alt="CBDoodle" />
      <div><h1>CBDoodle Dosing Chart</h1><p>Golden-Years Support for Every Doodle</p></div>
    </div>
    <p class="intro">A friendly starting guide for senior poodles and doodle mixes, based on typical vet-informed ranges of about <strong>0.1–0.5 mg CBD per kg</strong> of body weight, once or twice daily. Always start at the low end and go slow. Soft-chew counts assume a 2.5&nbsp;mg concept chew.</p>
    <table>
      <thead><tr><th>Weight band</th><th>Typical doodles</th><th>Suggested daily CBD</th><th>As soft chews</th></tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <div class="note"><strong>Important:</strong> This chart is illustrative concept guidance only — not a prescription. CBDoodle is not intended to diagnose, treat, cure, or prevent any disease. Please review with your veterinarian before starting, especially for seniors, dogs on other medications, or those with liver conditions. Established conditions (e.g. osteoarthritis) may call for a higher dose under veterinary guidance.</div>
    <div class="vet">
      <div>Veterinarian notes &amp; recommended dose</div>
      <div>Date</div>
    </div>
    <p class="foot">CBDoodle — a concept showcase. cbdoodle.com · Bring this chart to your next vet visit.</p>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };<\/script>
  </body></html>`);
  w.document.close();
  w.focus();
};

export const HowToUse = () => {
  const [weight, setWeight] = useState([25]); // lbs
  const lbs = weight[0];
  const kg = lbs / 2.2046;
  const low = kg * 0.1;
  const comfort = kg * 0.5;
  const chewsPerDay = Math.max(1, Math.round(comfort / 2.5));
  const fmt = (n) => (n < 1 ? n.toFixed(2) : n.toFixed(1));

  return (
    <section id="dosing" className="bg-[#FAF7F2] px-5 py-20 sm:px-8 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal><Label>How To Use</Label></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-[#2C2B29] sm:text-4xl lg:text-[2.9rem]">
              Gentle dosing, made simple
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-4 max-w-lg font-sans leading-relaxed text-[#5C5850]">
              A friendly starting guide based on typical vet-informed ranges. Always start low, go slow, and
              consult your veterinarian — especially for seniors on other medications.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-5 lg:gap-10">
          {/* Calculator */}
          <div data-testid="cbdoodle-dosing-calculator" className="lg:col-span-3">
            <div className="rounded-[2rem] border border-[#E6E0D4] bg-[#FFFDF9] p-7 shadow-sm sm:p-10">
              <div className="flex items-center justify-between">
                <span className="font-sans text-sm font-medium text-[#5C5850]">Your poodle's weight</span>
                <span className="font-serif text-3xl font-semibold text-[#2D4338]">
                  {lbs} <span className="text-lg text-[#8A8478]">lbs</span>
                </span>
              </div>

              <div className="mt-6">
                <Slider
                  data-testid="cbdoodle-dosing-weight-slider"
                  value={weight}
                  onValueChange={setWeight}
                  min={3}
                  max={80}
                  step={1}
                  className="py-2"
                />
                <div className="mt-2 flex justify-between font-mono text-[11px] uppercase tracking-wide text-[#8A8478]">
                  <span>Toy · 3 lbs</span>
                  <span>Standard · 80 lbs</span>
                </div>
              </div>

              <div data-testid="cbdoodle-dosing-result" className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-[#E2ECE7] p-5">
                  <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-[#4A6B5D]">
                    <Droplets size={14} /> Suggested daily CBD
                  </div>
                  <div className="mt-2 font-serif text-3xl font-semibold text-[#2D4338]">
                    {fmt(low)}–{fmt(comfort)} <span className="text-lg">mg</span>
                  </div>
                  <p className="mt-1 font-sans text-xs text-[#5C5850]">Start at the low end, adjust toward comfort.</p>
                </div>
                <div className="rounded-2xl bg-[#FDF6E7] p-5">
                  <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-[#B07D2B]">
                    <Cookie size={14} /> As soft chews
                  </div>
                  <div className="mt-2 font-serif text-3xl font-semibold text-[#B07D2B]">
                    ~{chewsPerDay} <span className="text-lg">chew{chewsPerDay > 1 ? "s" : ""}/day</span>
                  </div>
                  <p className="mt-1 font-sans text-xs text-[#8A6a25]">Based on a 2.5&nbsp;mg concept chew.</p>
                </div>
              </div>

              <div className="mt-6 flex items-start gap-2.5 rounded-xl bg-[#F3EDE2] px-4 py-3">
                <Info size={16} className="mt-0.5 flex-none text-[#4A6B5D]" />
                <p className="font-sans text-xs leading-relaxed text-[#5C5850]">
                  Illustrative concept guidance only (~0.1–0.5&nbsp;mg/kg). Not a prescription. Your vet may
                  recommend a different amount for established conditions.
                </p>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="lg:col-span-2">
            <div className="flex h-full flex-col justify-between gap-4">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.title} delay={i * 0.1}>
                    <div className="flex gap-4 rounded-2xl border border-[#E6E0D4] bg-[#FFFDF9] p-6">
                      <div className="flex-none">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#4A6B5D] text-white">
                          <Icon size={20} />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-serif text-lg font-semibold text-[#2C2B29]">
                          {i + 1}. {s.title}
                        </h3>
                        <p className="mt-1 font-sans text-sm leading-relaxed text-[#5C5850]">{s.body}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div id="dosing-chart" data-testid="cbdoodle-dosing-chart" className="mt-8 overflow-hidden rounded-[2rem] border border-[#E6E0D4] bg-[#FFFDF9] shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E6E0D4] px-6 py-5 sm:px-8">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#E2ECE7] text-[#4A6B5D]">
                  <FileText size={18} />
                </span>
                <div>
                  <h3 className="font-serif text-xl font-semibold text-[#2C2B29]">Doodle-by-weight chart</h3>
                  <p className="font-sans text-xs text-[#8A8478]">Print it and take it to your vet visit</p>
                </div>
              </div>
              <button
                data-testid="cbdoodle-print-chart-btn"
                onClick={printChart}
                className="inline-flex items-center gap-2 rounded-full bg-[#2D4338] px-6 py-3 font-sans text-sm font-medium text-white transition-all duration-200 hover:bg-[#203028] active:scale-[0.98]"
              >
                <Printer size={16} /> Print for your vet
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left">
                <thead className="bg-[#F3EDE2] font-mono text-[11px] uppercase tracking-[0.12em] text-[#8A8478]">
                  <tr>
                    <th className="px-6 py-3.5">Weight band</th>
                    <th className="px-6 py-3.5">Typical doodles</th>
                    <th className="px-6 py-3.5">Suggested daily CBD</th>
                    <th className="px-6 py-3.5">As soft chews</th>
                  </tr>
                </thead>
                <tbody className="font-sans text-sm">
                  {CHART_ROWS.map((r) => (
                    <tr key={r.label} className="border-t border-[#E6E0D4] even:bg-[#FBF8F2]">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-[#2C2B29]">{r.label}</div>
                        <div className="mt-0.5 font-mono text-[11px] text-[#8A8478]">{r.low}–{r.high} lbs</div>
                      </td>
                      <td className="px-6 py-4 text-[#5C5850]">{r.doodles}</td>
                      <td className="px-6 py-4 font-semibold text-[#2D4338]">{r.cbd}</td>
                      <td className="px-6 py-4 font-semibold text-[#B07D2B]">{r.chews}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
